import type { MyCawanModule } from "@/shared/model/modules/MyCawanModule";
import type { Domain, Site } from "@/shared/model/user/UserDomains";
import { keyFnToComparator } from "@/shared/utils/Comparator";

export class UserPermissions {
  constructor(
    readonly permissionsByDomainId: Map<string, UserDomainPermissions>,
    readonly flags: UserFlags,
  ) {}

  isEmpty(): boolean {
    return this.permissionsByDomainId.size === 0;
  }

  domainsPermissions(): UserDomainPermissions[] {
    return Array.from(this.permissionsByDomainId.values());
  }

  ofDomainId(domainId: string): UserDomainPermissions | undefined {
    return this.permissionsByDomainId.get(domainId);
  }

  domain(domainId: string): Domain | undefined {
    return this.ofDomainId(domainId)?.domain;
  }

  domains(): Domain[] {
    return this.filteredDomains(() => true);
  }

  adminOnAnyDomain(): boolean {
    return this.domainsWhereAdmin.length > 0;
  }

  domainsWhereAdmin(): Domain[] {
    return this.filteredDomains((udp) => udp.isAdmin);
  }

  domainsWhereAdminOrSiteAdmin(): Domain[] {
    return this.filteredDomains((udp) => {
      return udp.isAdmin || udp.hasOneOfSiteAdminRight();
    });
  }

  findSite(siteId: string): Site | undefined {
    for (const domainPermissions of this.permissionsByDomainId.values()) {
      const site = domainPermissions.domain.findSite(siteId);
      if (site !== undefined) return site;
    }
    return undefined;
  }

  canAccessSiteId(siteId: string): boolean {
    for (const domainPermissions of this.permissionsByDomainId.values()) {
      if (domainPermissions.canAccessSiteId(siteId)) return true;
    }
    return false;
  }

  permissionsOfDomainsWithModule(
    module: MyCawanModule,
  ): UserDomainPermissions[] {
    return this.filteredDomainsPermissions((udp) =>
      udp.domain.hasModule(module),
    );
  }

  hasModule(module: MyCawanModule): boolean {
    return this.domains().some((d) => d.hasModule(module));
  }

  domainsWithPermission(permission: string): Domain[] {
    return this.domainsWithAnyPermission([permission]);
  }

  hasPermissionOnAnyDomain(permission: string): boolean {
    return this.domainsWithPermission(permission).length > 0;
  }

  domainsWithEveryPermissions(permissions: string[]): Domain[] {
    return this.filteredDomains((udp) => udp.hasEveryPermissions(permissions));
  }

  domainsWithAnyPermission(permissions: string[]): Domain[] {
    return this.filteredDomains((udp) => udp.hasAnyPermission(permissions));
  }

  private filteredDomainsPermissions(
    filter: (udp: UserDomainPermissions) => boolean,
  ): UserDomainPermissions[] {
    return Array.from(this.permissionsByDomainId.values()).filter(filter);
  }

  private filteredDomains(
    filter: (udp: UserDomainPermissions) => boolean,
  ): Domain[] {
    return this.filteredDomainsPermissions(filter)
      .map((udp) => udp.domain)
      .sort(keyFnToComparator((d) => d.name.trim()));
  }
}

/**
 * Flags are implied as follows:
 * developper => support => commercial => beta_tester
 */
export class UserFlags {
  private constructor(
    private readonly developper: boolean,
    private readonly support: boolean,
    private readonly salesPerson: boolean,
    private readonly betaTester: boolean,
  ) {}

  static createDevelopper(): UserFlags {
    return new UserFlags(true, false, false, false);
  }

  static createSupport(): UserFlags {
    return new UserFlags(false, true, false, false);
  }

  static createSalesPerson(): UserFlags {
    return new UserFlags(false, false, true, false);
  }

  static createBetaTester(): UserFlags {
    return new UserFlags(false, false, false, true);
  }

  static createNoFlags(): UserFlags {
    return new UserFlags(false, false, false, false);
  }

  isDevelopper(): boolean {
    return this.developper;
  }

  isSupport(): boolean {
    return this.isDevelopper() || this.support;
  }

  isSalesPerson(): boolean {
    return this.isSupport() || this.salesPerson;
  }

  isBetaTester(): boolean {
    return this.isSalesPerson() || this.betaTester;
  }
}

export class UserDomainPermissions {
  constructor(
    public readonly domain: Domain,
    public readonly isAdmin: boolean,
    public readonly permissions: string[],
  ) {}

  canAccessSiteId(siteId: string): boolean {
    return this.domain.sites.some((s) => s.id === siteId);
  }

  hasPermission(permission: string): boolean {
    return this.isAdmin || this.permissions.includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.permissions.includes(p));
  }

  hasEveryPermissions(permissions: string[]): boolean {
    return permissions.every((p) => this.permissions.includes(p));
  }

  hasOneOfSiteAdminRight(): boolean {
    return (
      this.canManageInvitations() ||
      this.canUpdateSiteInfos() ||
      this.canManageMaterielMobile() ||
      this.canManagePendingUsers() ||
      this.canManageUserRights() ||
      this.canManageUserGroups() ||
      this.canManageNotifications()
    );
  }

  canManageInvitations(): boolean {
    return this.hasAnyPermission(["INVITATIONS"]);
  }

  canUpdateSiteInfos(): boolean {
    return this.hasAnyPermission(["SITES_INFOS_MODIF"]);
  }

  canManageMaterielMobile(): boolean {
    return this.hasAnyPermission(["MATERIEL_MOBILE_MODIF"]);
  }

  canManagePendingUsers(): boolean {
    return this.hasAnyPermission(["MANAGE_PENDING_USER"]);
  }

  canManageUserRights(): boolean {
    return this.hasAnyPermission(["MANAGE_USER_RIGHTS"]);
  }

  canManageUserGroups(): boolean {
    return this.hasAnyPermission(["MANAGE_USER_GROUPS"]);
  }

  canManageNotifications(): boolean {
    return this.hasAnyPermission(["NOTIFICATIONS"]);
  }
}
